import { type ComponentProps, memo, type ReactNode } from 'react';

import { cn } from '@air/utils-tailwind';

export type DiscussionsCommentProps = ComponentProps<'article'> & {
  actions?: ReactNode;
  avatar?: ReactNode;
  author: ReactNode;
  comment: ReactNode;
  meta?: ReactNode;
  secondaryActions?: ReactNode;
  timestamp?: string;
};

export const DiscussionsComment = memo(
  ({
    actions,
    author,
    avatar,
    className,
    comment,
    meta,
    secondaryActions,
    timestamp,
    ...restOfProps
  }: DiscussionsCommentProps) => {
    return (
      <article
        className={cn('group/discussions-comment flex w-full items-start gap-3 p-5', className)}
        data-testid="DISCUSSIONS_COMMENT"
        {...restOfProps}
      >
        {avatar}
        <div className="flex grow flex-col gap-1">
          <header className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
              <div
                className="shrink-0 truncate font-medium ads:text-primary text-14 text-gray-12"
                data-testid="DISCUSSIONS_COMMENT_AUTHOR"
              >
                {author}
              </div>
              {meta && <div className="shrink-0 ads:text-metadata text-12 text-gray-10">{meta}</div>}
              {!!timestamp && (
                <>
                  &middot;
                  <time
                    className="shrink-0 ads:text-metadata text-12 text-gray-10"
                    data-testid="DISCUSSIONS_COMMENT_TIMESTAMP"
                    dateTime={timestamp}
                  >
                    {timestamp}
                  </time>
                </>
              )}
            </div>

            {actions && (
              <div className="shrink-0 gap-1 ads:text-metadata text-12 text-gray-10 opacity-0 group-hover/discussions-comment:opacity-100">
                {actions}
              </div>
            )}
          </header>

          <section className="ads:text-secondary text-14 text-gray-11">{comment}</section>

          {secondaryActions && <div className="flex gap-2">{secondaryActions}</div>}
        </div>
      </article>
    );
  },
);

DiscussionsComment.displayName = 'DiscussionsComment';
